import React, { useState } from 'react';
import {initialReviews} from "./reviewsData"

const Reviews = () => {
  // State for reviews and visible count
  const [reviews, setReviews] = useState(initialReviews);
  const [visibleCount, setVisibleCount] = useState(6);
  const itemsPerPage = 6;

  // Function to generate avatar from name
  const getAvatarFromName = (name) => {
    if (!name) return '?';
    const nameParts = name.split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
  };

  // Function to generate background color from name (consistent)
  const getAvatarColor = (name) => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
    ];
    
    // Simple hash function for consistent color assignment
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Star Rating Component
  const StarRating = ({ rating }) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`w-3 h-3 sm:w-4 sm:h-4 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-xs text-gray-500">{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Load more reviews
  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + itemsPerPage, reviews.length));
  };

  // Show less reviews - resets to initial count
  const handleShowLess = () => {
    setVisibleCount(itemsPerPage);
  };

  // Calculate average rating
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  // Get visible reviews
  const visibleReviews = reviews.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Customer Reviews
          </h1>
          <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto">
            Read what our satisfied customers are saying about their experience
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center items-center gap-3 mt-4">
            <div className="bg-white px-3 py-2 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="text-xl font-bold text-gray-900 mr-1">{averageRating}</div>
                <StarRating rating={Math.round(parseFloat(averageRating))} />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Average Rating</p>
            </div>
            <div className="bg-white px-3 py-2 rounded-lg shadow-sm">
              <div className="text-xl font-bold text-gray-900">{reviews.length}</div>
              <p className="text-xs text-gray-500 mt-0.5">Total Reviews</p>
            </div>
          </div>
        </div>

        {/* Masonry Layout Container */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5 space-y-4 sm:space-y-5">
          {visibleReviews.map((review) => (
            <div key={review.id} className="break-inside-avoid">
              <div className="bg-white rounded-lg p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                {/* Review Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className={`${getAvatarColor(review.name)} w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base mr-2 sm:mr-3`}>
                      {getAvatarFromName(review.name)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base">{review.name}</h3>
                      <StarRating rating={review.rating} />
                    </div>
                  </div>
                </div>

                {/* Review Comment */}
                <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">
                  {review.comment}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Load More / Show Less Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-6 sm:mt-8">
          {visibleCount < reviews.length && (
            <button
              onClick={handleLoadMore}
              className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm"
            >
              Load More ({reviews.length - visibleCount} more reviews)
            </button>
          )}
          
          {visibleCount > itemsPerPage && (
            <button
              onClick={handleShowLess}
              className="w-full sm:w-auto px-5 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200 text-sm"
            >
              Show Less
            </button>
          )}
        </div>

        {/* Showing count */}
        <div className="text-center mt-4 text-gray-500 text-xs">
          Showing {visibleReviews.length} of {reviews.length} reviews
        </div>
      </div>
    </div>
  );
};

export default Reviews;